import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Columns, LoadingModule,
} from '@domain-group/suit-theme'

import useAccountingIntegration, { XeroInvoice } from '@hooks/useAccountingIntegration'
import useSelector from '@hooks/useSelector'

import AccountingIntegrationButton from '@components/AccountingIntegrationButton'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext, { DefaultContext, PageContextPayload } from '@contexts/pageContext'

import generateNavigationItems from
  '@sections/Client/packs/Accounting/components/AccountingNavigation/utils/generateNavigationItems'

import type { InvoiceModel } from '@models/types'

import InvoiceListItem from '../InvoiceListItem'
import InvoicesImportBlankState from '../InvoicesImportBlankState'

const invoiceSynced = (invoiceArray: InvoiceModel[], invoice: XeroInvoice) => {
  const syncedInvoice = !!invoiceArray.find(i => digObject(i, 'data.xero_id') === invoice.external_invoice_id)
  return !!syncedInvoice
}

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'start_date',
      label: 'Created After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'end_date',
      label: 'Created Before',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'status',
      label: 'Status',
      values: [
        { key: 'AUTHORISED', label: 'Authorised' },
        { key: 'DRAFT', label: 'Draft' },
        { key: 'PAID', label: 'Paid' },
        { key: 'SUBMITTED', label: 'Submitted' },
        { key: 'VOIDED', label: 'Voided' },
      ],
    },
  ]
}

interface PageFilters {
  start_date?: string,
  end_date?: string,
  status?: string,
}

const PageContent = (props: { pageFilters: PageFilters }) => {
  const { pageFilters } = props

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const accountingIntegrationPayload = useAccountingIntegration({
    externalInvoiceFilters: { ...pageFilters },
    shouldFetchIntegrationData: true,
    shouldSearchExternalInvoices: true,
  })

  const {
    externalInvoices,
    hasAccountingIntegrationInvoices,
    hasActiveSession,
    loadingExternalInvoices,
    selectedAgencyId,
    selectedIntegrationId,
  } = accountingIntegrationPayload

  // @ts-expect-error TS2345
  const nav = generateNavigationItems(selectedAgencyId)
  const items = digObject(nav.sections[0], 'items')

  const invoices = useSelector(reduxState => reduxState.entities.invoices)
  const invoiceArray = Object.values(invoices)

  return (
    <>
      <Helmet>
        <title>Invoice Import | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={
          (!selectedIntegrationId || hasActiveSession) && (
            // @ts-expect-error TS2741
            <AccountingIntegrationButton accountingPayload={accountingIntegrationPayload} />
          )
        }
        activeTabBarItemKey="import-invoices"
        nestedNavigation
        tabBarItems={items}
        title="Invoice Import"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.invoiceImport.banner" />

            <LoadingModule loading={loadingExternalInvoices} times={3} />

            {!loadingExternalInvoices && !hasAccountingIntegrationInvoices && <InvoicesImportBlankState />}

            {!loadingExternalInvoices && externalInvoices.map((invoice) => {
              if (!invoiceSynced(invoiceArray, invoice)){
                return (
                  <InvoiceListItem
                    // @ts-expect-error TS2322
                    agencyId={selectedAgencyId}
                    key={invoice.xero_invoice_number}
                    xeroInvoice={invoice}
                  />
                )
              }
            })}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.invoiceImport.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
